import AdminPermission from "components/admin/AdminPermission";
import {
  httpExportSettlements,
  httpFetchSettlements,
  httpResolveSettlement,
  SettlementCountry,
  SettlementStatus,
} from "api/admin/settlements";
import AdminSidebar from "components/layout/AdminSidebar";
import Container from "components/layout/Container";
import Main from "components/layout/Main";
import Filter from "components/ui/Filter";
import Pagination from "components/ui/Pagination";
import Spinner from "components/ui/Spinner";
import { DateTime } from "luxon";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { moneyFormat } from "utilities/currency";

const limit = 50;

const countryTabs: Array<{ label: string; value: SettlementCountry }> = [
  { label: "Nigeria", value: "NG" },
  { label: "Ghana", value: "GH" },
  { label: "Kenya", value: "KE" },
];

const statusFilters: Array<{ label: string; value: SettlementStatus }> = [
  { label: "All", value: "all" },
  { label: "Credited", value: "credited" },
  { label: "Pending", value: "pending" },
  { label: "Failed", value: "failed" },
];

const isSettlementStatus = (value: string): value is SettlementStatus =>
  statusFilters.some((status) => status.value === value);

const statusClassName = (status: string) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "credited") return "bg-green-50 text-green-700";
  if (normalized === "pending") return "bg-yellow-50 text-yellow-700";
  if (normalized === "resolved") return "bg-blue-50 text-blue-700";
  return "bg-red-50 text-red-700";
};

const downloadCsv = ({ csv, filename }: { csv: string; filename: string }) => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  window.URL.revokeObjectURL(url);
};

const Settlements: React.FC = () => {
  const queryClient = useQueryClient();
  const [country, setCountry] = useState<SettlementCountry>("NG");
  const [status, setStatus] = useState<SettlementStatus>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [resolutionNotes, setResolutionNotes] = useState<
    Record<number, string>
  >({});

  const { data, isLoading } = useQuery(
    ["settlements", country, status, currentPage],
    () => httpFetchSettlements(country, currentPage, limit, status),
    {
      initialData: {
        rows: [],
        total: 0,
        page: 1,
      },
    },
  );

  const resolveMutation = useMutation(
    ({ settlementID, note }: { settlementID: number; note: string }) =>
      httpResolveSettlement(settlementID, note),
    {
      onSuccess: () => {
        setResolutionNotes({});
        queryClient.invalidateQueries(["settlements"]);
      },
    },
  );

  const exportMutation = useMutation(
    () => httpExportSettlements(country, status),
    {
      onSuccess: (exportData: any) => {
        downloadCsv({
          csv: exportData.csv,
          filename: exportData.filename,
        });
      },
    },
  );

  const rows = data?.rows || [];

  return (
    <Container>
      <AdminSidebar />
      <Main className="mx-5 mt-5 text-left">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h3 className="text-2xl">Settlements</h3>
            <p className="mt-2 text-sm text-gray-500">
              Reconcile bank and mobile-money settlement credits by market.
            </p>
          </div>
          <AdminPermission permission={"settlements.settlements.export"}>
            <button
              className="px-4 py-2 text-sm font-medium text-white bg-black rounded disabled:opacity-40"
              disabled={exportMutation.isLoading}
              onClick={() => exportMutation.mutate()}
              type="button"
            >
              {exportMutation.isLoading ? "Exporting..." : "Export CSV"}
            </button>
          </AdminPermission>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 mt-8">
          <div className="flex flex-wrap gap-2">
            {countryTabs.map((tab) => (
              <button
                className={`rounded px-4 py-2 text-sm font-medium ${
                  country === tab.value
                    ? "bg-black text-white"
                    : "bg-orange-50 text-gray-700"
                }`}
                key={tab.value}
                onClick={() => {
                  setCountry(tab.value);
                  setCurrentPage(1);
                }}
                type="button"
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="min-w-[220px]">
            <div className="mb-2 text-sm text-gray-600">Credit status</div>
            <Filter
              data={statusFilters}
              setValue={(value: string) => {
                if (isSettlementStatus(value)) {
                  setStatus(value);
                  setCurrentPage(1);
                }
              }}
              value={status}
            />
          </div>
        </div>

        <div className="mt-8 overflow-x-auto bg-white border border-gray-100 rounded">
          {isLoading ? (
            <div className="flex items-center justify-center w-full h-64">
              <Spinner className="text-5xl" />
            </div>
          ) : rows.length ? (
            <table className="w-full min-w-[1100px] text-sm">
              <thead className="text-xs tracking-wide text-left text-gray-500 uppercase bg-gray-50">
                <tr>
                  <th className="px-4 py-3">Reference</th>
                  <th className="px-4 py-3">User</th>
                  <th className="px-4 py-3">Method</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Currency</th>
                  <th className="px-4 py-3">Credit Status</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Failed Reason</th>
                  <th className="px-4 py-3">Resolution</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row: any) => {
                  const isFailed = row.creditStatus === "Failed";
                  const note = resolutionNotes[row.id] || "";
                  return (
                    <tr key={row.id} className="align-top">
                      <td className="px-4 py-4 font-medium text-gray-900">
                        {row.transactionReference}
                      </td>
                      <td className="px-4 py-4">
                        {row.userName
                          ? `@${row.userName}`
                          : `User ${row.userID}`}
                      </td>
                      <td className="px-4 py-4">{row.paymentMethod}</td>
                      <td className="px-4 py-4">{moneyFormat(row.amount)}</td>
                      <td className="px-4 py-4">{row.currency}</td>
                      <td className="px-4 py-4">
                        <span
                          className={`rounded-full px-3 py-1 text-xs font-semibold ${statusClassName(
                            row.creditStatus,
                          )}`}
                        >
                          {row.creditStatus}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        {row.date
                          ? `${DateTime.fromISO(row.date).toFormat(
                              "DD",
                            )} ${DateTime.fromISO(row.date).toFormat("t")}`
                          : "-"}
                      </td>
                      <td className="px-4 py-4 text-gray-600">
                        {row.failureReason || "-"}
                      </td>
                      <td className="px-4 py-4">
                        {row.resolution?.note ? (
                          <div>
                            <div className="font-medium text-gray-900">
                              {row.resolution.note}
                            </div>
                            <div className="mt-1 text-xs text-gray-500">
                              {row.resolution.resolvedAt
                                ? DateTime.fromISO(
                                    row.resolution.resolvedAt,
                                  ).toFormat("DD t")
                                : "Resolved"}
                            </div>
                          </div>
                        ) : isFailed ? (
                          <AdminPermission permission={"settlements.settlements.approve"}>
                            <div className="w-72">
                              <textarea
                                className="w-full h-20 p-2 text-sm border border-gray-200 rounded outline-none resize-none focus:border-orange-300"
                                onChange={(event) =>
                                  setResolutionNotes((current) => ({
                                    ...current,
                                    [row.id]: event.target.value,
                                  }))
                                }
                                placeholder="Resolution note"
                                value={note}
                              />
                              <button
                                className="mt-2 rounded bg-black px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
                                disabled={!note.trim() || resolveMutation.isLoading}
                                onClick={() =>
                                  resolveMutation.mutate({
                                    settlementID: row.id,
                                    note,
                                  })
                                }
                                type="button"
                              >
                                Mark resolved
                              </button>
                            </div>
                          </AdminPermission>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="py-10 text-center text-gray-500">
              No settlements found
            </div>
          )}
        </div>

        <Pagination
          setCurrentPage={setCurrentPage}
          currentPage={currentPage}
          limit={limit}
          page={data?.page || 1}
          total={data?.total || 0}
        />
      </Main>
    </Container>
  );
};

export default Settlements;
